import { WorkRolesEnum, WorkUseremploymentSalaryIntervalEnum } from '@work-frontend-monorepo/work-rest-api';

/** Ein Standort des Mandanten, wie ihn Fussbereiche und Kontaktseiten anzeigen. */
export interface TenantLocation {
  /** Region oder Niederlassungsname, z.B. "Nordwestschweiz". */
  region: string;
  street: string;
  /** PLZ und Ort samt Kantonskürzel, z.B. "4442 Diepflingen/BL". */
  zipCity: string;
  /** Google-Maps-Link. Leer = die Adresse wird ohne Link angezeigt. */
  mapsUrl: string;
}

/**
 * Firebase-Web-App für Browser-Pushes im work-office. Jeder Mandant hat ein eigenes Firebase-Projekt — passend zu den
 * FIREBASE_*-Server-Zugangsdaten der API desselben Mandanten. Die Werte sind öffentlich (gehören ins Frontend).
 */
export interface FirebaseWebPushConfig {
  apiKey: string;
  projectId: string;
  messagingSenderId: string;
  appId: string;
  /** Öffentlicher Schlüssel des Web-Push-Zertifikats (Firebase → Cloud Messaging → Web-Push-Zertifikate). */
  vapidKey: string;
}

/**
 * This global environment variables are equal to production.
 * The GLOBAL constant shouldn't be imported directly. Only import them via an environment variable.
 */
/** Technischer Schlüssel des Mandanten, z.B. für mandantenspezifische Seiten. */
export type TenantKey = 'axova' | 'sfg' | 'sonnwende';

export const GLOBALS = {
  /**
   * Environment name.
   */
  tenantKey: '' as TenantKey | '',
  tenantCompany: '',
  tenantCompanyCommonName: '',
  tenantAdministrationEmail: '',
  tenantLogoPath: '',
  tenantOfficeLogoPath: '',
  tenantHeroGraphicOrIcon: '',
  tenantFaviconPath: '',

  /**
   * Modules control.
   */
  disabledModulesForTenant: [],

  /**
   * Name des i18n-Override-Moduls dieses Mandanten, z.B. 'SFG-OVERRIDES' — entspricht dem Ordner
   * unter assets/i18n/. Die Overrides werden nach den globalen Keys geladen und gewinnen deshalb.
   * Leer = der Mandant nutzt ausschliesslich assets/i18n/de.json.
   */
  i18nOverrideModule: '',

  /**
   * Environment name.
   */
  name: 'production',

  /**
   * Can be used to check if it is the one and only production environment.
   */
  production: true,

  /**
   * API Base URL. Defined per customer in base.ts environment file.
   */
  apiConfiguration: {
    baseUrl: '',
    publicApiKey: '',
  },

  /**
   * URLs to Apps. Defined per customer in base.ts environment file.
   */
  /**
   * Standorte des Mandanten. Leer = der Standort-Block wird nicht angezeigt.
   */
  locations: [] as TenantLocation[],

  /**
   * Rechtliche Seiten des Mandanten. Absolute URLs, weil sie aus anderen Apps heraus verlinkt
   * werden. Leer = der Link wird nicht angezeigt (besser als ein toter Link) — pro Mandant
   * eintragen, sobald die Pfade bekannt sind.
   */
  legalUrls: {
    imprint: '',
    privacy: '',
  },

  appUrls: {
    workOffice: '',
    workOfficeV4: '',
    app: '',
    appointmentScheduler: '',
    files: '',
    website: '',
    geruestrechner: '',
    /**
     * Basis der Projektfreigabe-App. Nur zur Referenz und für Verlinkungen aus anderen Apps — die
     * geteilten Links baut der Server aus `PROJECT_SHARE_BASE_URL`, damit es dafür genau eine
     * Quelle gibt.
     */
    freigabe: '',
  },

  /**
   * Website specific
   */
  website: {
    recaptchaKey: '',
  },

  /**
   * Digitale Menschen Support-Widget.
   * `apiKey`, `appName`, `primaryColor`, `title`, `subtitle` werden pro Mandant
   * in den *_GLOBALS überschrieben. `apiBaseUrl`, `loaderUrl` und `position`
   * sind für alle Mandanten identisch.
   * `cacheWindowMs` steuert das Cache-Busting-Zeitfenster des Loaders.
   */
  support: {
    apiKey: '',
    apiBaseUrl: 'https://support.digitalemenschen.app/api',
    loaderUrl: 'https://support.digitalemenschen.app/support-widget.js',
    appName: '',
    primaryColor: '',
    title: '',
    subtitle: '',
    position: { bottom: 100, right: 24 } as { top?: number; bottom?: number; left?: number; right?: number },
    launcher: { variant: 'tab', edge: 'right', label: 'Support' } as {
      variant: 'tab' | 'button';
      edge: 'left' | 'right' | 'bottom' | 'top';
      label: string;
    },
    cacheWindowMs: 60 * 60 * 1000,
  },

  /**
   * Language configuration, setting the default language and provide a list of available languages in the app.
   */
  languagesDefault: 'de',
  languagesAvailable: ['de'],

  /**
   * Firebase configuration.
   */
  firebase: {
    configuration: {},
  },

  /**
   * Logging settings for NGXS and general.
   */
  isLoggingEnabled: {
    logger: false,
    ngxs: false,
  },

  /**
   * Work Office specific. Defined per customer in base.ts environment file.
   */
  workOffice: {
    appName: '',
    topNavigationLogoVariant: '',
  },

  workStates: {
    names: {
      favorites: '',
      filter: '',
      loading: '',
      profile: '',
      reload: '',
      sfgGeruest: '',
      tenants: '',
      terminAssistent: '',
    },
  },

  /**
   * Sentry
   */
  sentryDsn: {
    app: '',
    screen: '',
    website: '',
    office: '',
    geruestrechner: '',
    /**
     * Basis der Projektfreigabe-App. Nur zur Referenz und für Verlinkungen aus anderen Apps — die
     * geteilten Links baut der Server aus `PROJECT_SHARE_BASE_URL`, damit es dafür genau eine
     * Quelle gibt.
     */
    freigabe: '',
  },

  standardCategoryIdForNewContacts: 4,

  /**
   * Default-Werte für Lead-Kategorisierung beim Erstellen eines Kontakts aus einer Reservation.
   * Wenn gesetzt, werden diese IDs als `leadcategoryId` / `leadsubcategoryId` ins Create-Form vorbelegt.
   * `null` bedeutet keine Vorbelegung.
   */
  reservationLeadDefaults: { leadcategoryId: undefined, leadsubcategoryId: undefined },

  /**
   * Verkehrsbussen-Modul.
   * Wenn gesetzt, wird das Modul (sfg-app "Mehr"-Menü + work-office Lohnlauf-Tab) aktiviert
   * und filtert Documents nach dieser documentsubtypeId.
   * `null` = Modul ist deaktiviert für diesen Mandanten.
   */
  verkehrsbussenDocumentsubtypeId: null as number | null,

  /**
   * Default-Werte für neue Arbeitsverhältnisse (Mitarbeiter → Anstellungsverhältnis erstellen).
   * Pro Mandant überschrieben: `targetHours` = Sollstunden bei 100%, `holidaysPerYear` = Ferientage pro Jahr bei 100%,
   * `salaryInterval` = Lohnintervall ('MONTHS12' oder 'MONTHS13').
   */
  useremploymentDefaults: {
    targetHours: 8.5,
    holidaysPerYear: 27,
    salaryInterval: WorkUseremploymentSalaryIntervalEnum.Months13,
  },

  /**
   * Kundenberater (`userId`) beim Erstellen eines neuen Projekts vorbelegen.
   * `true`: immer der eingeloggte User.
   * `false`: Kundenberater des vorherigen Projekts (1. Prio), sonst `contact.userId` (2. Prio),
   * sonst der eingeloggte User (3. Prio).
   */
  useLoggedInUserAsDefaultProjectAdvisor: false,

  showObjectAddressInsteadOfCustomer: false,
  /** Montageplan: Kunde des verknüpften Partner-Mandanten standardmässig auf den Blöcken zeigen (User kann es umstellen). */
  montageplanShowExternalCustomerByDefault: false,
  /** Montageplan: Objektadresse standardmässig in der zweiten Blockzeile zeigen (User kann es umstellen). */
  montageplanShowObjectAddressByDefault: false,
  standardTaskIdsToShowInAppDashboard: [],
  /** App-Projektdetails: Einsatzarten (z.B. Montage/Demontage) als Gruppen von Standardtasks. Leer = Abschnitt ausgeblendet. */
  appProjectAssignmentTypes: [] as { label: string; standardtaskIds: number[] }[],
  /** App-Projektdetails: Kalkulation zusätzlich zu `calculations:read` auf diese Rollen beschränken. Leer = keine Einschränkung. */
  appProjectCalculationRequiredRoles: [] as WorkRolesEnum[],
  showMontageplanTasksOfNotSoldProjects: false,
  allowLockedMontageplanBlocksMoveOnYAxis: false,
  requireExternalProjectLead: false,
  showSkontoPercentage: false,
  qrNumberPrefix: '',
  skipContactToLeadModal: false,

  /**
   * Debitoren: Axova-Spezifika. Wenn true, werden `partial === 3` (fehlende Teile) und
   * `partial === 99` (KLEIV-Vorschüsse) als eigene Debitoren-Kategorien behandelt und die
   * entsprechenden Tabs unter Finanzen → Debitoren angezeigt (inkl. Zessionsliste-Export).
   * Bei anderen Tenants (false) zählen 3/99 als normale Debitoren und die Tabs entfallen.
   * Muss mit dem Backend-Flag `DEBTOR_PARTIAL_CATEGORIES_ENABLED` übereinstimmen.
   */
  showDebtorPartialCategories: false,

  /**
   * Debitoren: Button "Rechnung per E-Mail senden" in der Debitoren-Übersicht anzeigen.
   * Nur für SFG aktiviert (true), bei Axova ausgeblendet.
   */
  showDebtorSendInvoiceByMail: false,

  /**
   * Mandanten-Externe-Projekte Konfiguration.
   * Wenn gesetzt, wird die Mandanten-View "Externe Projekte" mit diesen Parametern angezeigt.
   */
  mandantenExterneProjekte: null as {
    roletypeName: string;
    calculationItemIds?: string;
    priceColumnLabel?: string;
    pageTitle: string;
    taskColumns: { label: string; standardtaskIds: number[] }[];
  } | null,

  /**
   * must be present because used in SFG_GLOBALS
   */
  workItemGroupDefault: '',
  workItemsKeysSolar: [],
  workItemsKeysFassade: [] as string[],
  workItemsKeyForAufgang: '',
  workItemsKeyForFirstFallProtection: '',
  workItemsKeyForRoofProtectionWall: '',
  workItemsKeyForKonsolen: '',
  workItemsKeysToDisplayDormers: [],
  workItemsKeyForOptionVorsprung: '',
  workItemsKeyForTower: '',
  workItemsKeyForInstallationTower: '',
  workItemsKeyForFlatSecurity: '',
  workItemsKeyForDormer: '',
  workItemsKeyForSideFallProtection: '',
  workItemsKeyForRowHouseFallProtection: '',
  workItemsKeyForRowHouseSideFallProtection: '',
  workItemsKeyForRoofInclinationOptions: [] as string[],
  workItemsForbiddenKeyForSolarOnly: [''],
  workProjectsStatusKeyEntwurf: '',
  workProjectStatusKeyAngefragt: '',
  workItemGroupOptions: '',

  /**
   * Google map api key (used in Projekte-Karte)
   */
  googleMapApiKey: 'AIzaSyDrmSp-J7iLyTt0HKP-iVYrWlYbOr4us7E', // only works on localhost

  /** `null` = Mandant ohne eigenes Firebase-Projekt → Browser-Pushes werden im work-office nicht angeboten. */
  firebaseWebPush: null as FirebaseWebPushConfig | null,
  googleMapSettings: {
    // sissach
    // initialCenter: { lat: 47.46252, lng: 7.813823 },
    // center of switzerland
    initialCenter: { lat: 46.8182, lng: 8.2275 },
    initialZoom: 8,
    markerSize: 40,
    mapId: 'ef264cd903521b34',
    navUpdateDelay: 30000,
  },
};
